/** Sort helpers for Admin Task > School List.

The list is paged (50–500), so — as on the Subscriptions tab — a header click
re-sorts the rows already on screen immediately and sends `order_by`/`order` on
the next list request, so the server window matches the column the operator
clicked.

`SCHOOL_SERVER_SORT_COLUMNS` is the live `get_filtered_school_list` allow-set
under `admin_view=1` (school_service._ADMIN_ALLOWED_ORDER). `matched_subscriptions`
is NOT in it: that column is assembled on the client and only exists while the
subscription keyword filter is on, where every sort is client-side anyway — so it
must be reset when that filter is switched off, or the next server request would
silently fall back to `id`. The action column is not sortable.
*/

export const DEFAULT_SCHOOL_SORT_BY = 'id';
export const DEFAULT_SCHOOL_SORT_DIR = 'DESC';

/** Columns the server can order by (admin_view=1). */
export const SCHOOL_SERVER_SORT_COLUMNS = [
  'id',
  'name',
  'name_eng',
  'key',
  'locale',
  'school_type',
  'school_level',
  'last_subscription_update',
  'epack_quota',
];

/** Client-only, keyword-filter-only. */
export const SCHOOL_KEYWORD_SORT_COLUMN = 'matched_subscriptions';

export const SCHOOL_SORT_COLUMNS = [
  ...SCHOOL_SERVER_SORT_COLUMNS,
  SCHOOL_KEYWORD_SORT_COLUMN,
];

// Recency and identity read newest/highest-first; names and codes read A-Z.
// epack_quota opens ASC deliberately: the question an admin asks of that column
// is "who is running out", so the first click should surface the lowest.
const DESC_FIRST_COLUMNS = new Set(['id', 'last_subscription_update']);

export function nextSchoolSort(sortBy, sortDir, column) {
  if (sortBy === column) {
    return {
      sortBy: column,
      sortDir: String(sortDir).toUpperCase() === 'ASC' ? 'DESC' : 'ASC',
    };
  }
  return {
    sortBy: column,
    sortDir: DESC_FIRST_COLUMNS.has(column) ? 'DESC' : 'ASC',
  };
}

/** True when `column` needs the subscription keyword filter to exist. */
export function isKeywordOnlySortColumn(column) {
  return column === SCHOOL_KEYWORD_SORT_COLUMN;
}

function dateMillis(value) {
  if (!value) {
    return null;
  }
  // MySQL hands back "YYYY-MM-DD HH:MM:SS"; Safari needs the T separator.
  const t = Date.parse(String(value).replace(' ', 'T'));
  return Number.isNaN(t) ? null : t;
}

function numberOrNull(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

function sortValue(row, column) {
  switch (column) {
    case 'id':
      return numberOrNull(row && row.id);
    case 'school_type':
      return numberOrNull(row && row.school_type);
    case 'school_level':
      return numberOrNull(row && row.school_level);
    case 'epack_quota':
      // Never null from the server, but a row from a pre-admin_view backend has
      // no such field — treat that as unknown rather than as zero quota.
      return numberOrNull(row && row.epack_quota);
    case 'last_subscription_update':
      return dateMillis(row && row.last_subscription_update);
    case SCHOOL_KEYWORD_SORT_COLUMN: {
      const list = (row && row.matched_subscriptions) || [];
      return list.length > 0 ? String(list[0]) : null;
    }
    default: {
      const raw = row && row[column];
      // '' is a real value here (school 935's blank `name`), but it should sort
      // with the other empties rather than ahead of every named school.
      return raw == null || raw === '' ? null : String(raw);
    }
  }
}

function compareValues(va, vb, dir) {
  // NULL sorts as the SMALLEST value — first on ASC, last on DESC. That is
  // MySQL's ordering, and it has to match: a header click re-sorts the rows on
  // screen here and then re-requests, so a client rule of "nulls always last"
  // would show one order for a moment and a different one once the server
  // answered. Schools with no subscription at all make this a common case on
  // last_subscription_update.
  if (va == null && vb == null) {
    return 0;
  }
  if (va == null) {
    return -1 * dir;
  }
  if (vb == null) {
    return 1 * dir;
  }
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * dir;
  }
  return String(va).localeCompare(String(vb), 'zh-Hant', {
    numeric: true,
    sensitivity: 'base',
  }) * dir;
}

/**
 * Sort school rows client-side. Used for the instant re-sort on a header click,
 * and as the ONLY sort under the subscription keyword filter, where rows are
 * merged from several requests and no single server query ordered them.
 */
export function sortSchoolRows(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? rows : [];
  const dir = String(sortDir).toUpperCase() === 'ASC' ? 1 : -1;
  const column = SCHOOL_SORT_COLUMNS.includes(sortBy) ? sortBy : DEFAULT_SCHOOL_SORT_BY;
  return [...list].sort((a, b) => {
    const cmp = compareValues(sortValue(a, column), sortValue(b, column), dir);
    if (cmp !== 0) {
      return cmp;
    }
    // Deterministic tiebreak, so equal keys don't reshuffle between searches.
    return ((numberOrNull(a && a.id) || 0) - (numberOrNull(b && b.id) || 0)) * dir;
  });
}
